import { ColumnsType } from 'antd/es/table';
import { StudentLoanRecordType } from '@nxsys/types/models/hmrcUpdates/studentLoan';
import AppIconButton from '@nxsys/components/AppIconButton';
import { IconCheckCircle, IconCloseCircle } from '@nxsys/components/AppIconSvg';
import moment from 'moment-timezone';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { formatStatus } from '@nxsys/utils';
import { Tag } from 'antd';

type StudentLoanPendingColumnsProps = {
  handleApply: (record: StudentLoanRecordType) => void;
  handleCancel: (record: StudentLoanRecordType) => void;
  messages: TypedMessages;
  checkBoxHeader: boolean;
  handleSelectAllRow: (e: any) => void;
  handleSelectRow: (id: number, checked: boolean) => void;
  selectedRowIds: number[];
  isBureau: boolean;
};

type StudentLoanAppliedCancelledColumnsProps = {
  messages: TypedMessages;
  isBureau: boolean;
  isAgency?: boolean;
  handleApply: (record: StudentLoanRecordType) => void;
  handleCancel: (record: StudentLoanRecordType) => void;
};

export const getStudentLoanPendingColumns = ({
  handleApply,
  handleCancel,
  messages,
  checkBoxHeader,
  handleSelectAllRow,
  handleSelectRow,
  selectedRowIds,
  isBureau,
}: StudentLoanPendingColumnsProps): ColumnsType<StudentLoanRecordType> => [
  {
    key: 'checkbox',
    title: (
      <AppCheckbox
        checked={checkBoxHeader}
        onChange={handleSelectAllRow}
        size="small"
      />
    ),
    fixed: 'left',
    width: 80,
    render: (record: StudentLoanRecordType) => (
      <AppCheckbox
        checked={selectedRowIds?.includes(record.id)}
        onChange={(e) => handleSelectRow(record.id, e.target.checked)}
        size="small"
      />
    ),
  },
  {
    title: messages['table.actions'],
    fixed: 'left',
    width: 100,
    render: (record: StudentLoanRecordType) => (
      <ul className="table-actions flex items-center justify-center">
        <li>
          <AppIconButton
            title={messages['common.apply']}
            icon={<IconCheckCircle color="#008000" width={20} height={20} />}
            onClick={handleApply.bind(this, record)}
          />
        </li>
        <li>
          <AppIconButton
            title={messages['common.cancel']}
            icon={<IconCloseCircle />}
            onClick={handleCancel.bind(this, record)}
          />
        </li>
      </ul>
    ),
  },
  {
    title: isBureau
      ? messages['common.employer']
      : messages['table.paymentCompany'],
    width: 250,
    dataIndex: 'request',
    render: (value) => value?.paymentCompany?.companyName || '_',
  },
  {
    title: messages['hmrcUpdates.hmrcId'],
    dataIndex: 'data',
    width: 250,
    render: (data) => data?.WorksNumber || '_',
  },
  {
    title: messages['hmrcUpdates.nxsysId'],
    width: 250,
    render: (record) => record?.payId || record?.previousPayrollNo,
  },
  {
    title: messages['table.employeeName'],
    width: 250,
    render: (record) => {
      const { data, subcontractorRef } = record;
      return (
        subcontractorRef?.fullName ||
        `${subcontractorRef?.forename || data?.Name?.Forename || ''} ${subcontractorRef?.surname || data?.Name?.Surname || ''}`.trim() ||
        '_'
      );
    },
  },
  {
    title: messages['table.niNumber'],
    width: 250,
    dataIndex: 'NINumber',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.dateIssued'],
    width: 250,
    dataIndex: 'issuedDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },
  {
    title: messages['common.type'],
    width: 250,
    dataIndex: 'type',
    render: (value: string) => value || '_',
  },
  {
    title: messages['common.startDate'],
    width: 250,
    dataIndex: 'startDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },
  {
    title: messages['common.endDate'],
    width: 250,
    dataIndex: 'endDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },

  {
    title: messages['hmrcUpdates.studentLoan.studentLoanPlanType'],
    width: 250,
    dataIndex: 'planType',
    render: (value) => value || '_',
  },
  {
    key: 'error',
    title: messages['hmrcUpdates.p6p9.errorMessage'],
    width: 250,
    dataIndex: 'message',
    render: (value) => value || '_',
  },
];

export const getStudentLoanAppliedCancelledColumns = ({
  messages,
  isBureau,
  isAgency,
  handleApply,
  handleCancel,
}: StudentLoanAppliedCancelledColumnsProps): ColumnsType<StudentLoanRecordType> => [
  ...(isAgency
    ? [
        {
          title: messages['table.actions'],
          fixed: 'left' as const,
          width: 100,
          render: (record: StudentLoanRecordType) => (
            <ul className="table-actions flex items-center justify-center">
              <li>
                <AppIconButton
                  title={messages['common.apply']}
                  icon={
                    <IconCheckCircle color="#008000" width={20} height={20} />
                  }
                  onClick={handleApply.bind(this, record)}
                />
              </li>
              <li>
                <AppIconButton
                  title={messages['common.cancel']}
                  icon={<IconCloseCircle />}
                  onClick={handleCancel.bind(this, record)}
                />
              </li>
            </ul>
          ),
        },
      ]
    : []),
  {
    key: 'status',
    title: messages['hmrcUpdates.p6p9.appliedCancelled'],
    width: 170,
    dataIndex: 'status',
    render: (value) => (
      <AppTagStatus className={`tag-popup ${formatStatus(value)}`}>
        {formatStatus(value)}
      </AppTagStatus>
    ),
  },
  {
    title: isBureau
      ? messages['common.employer']
      : messages['table.paymentCompany'],
    dataIndex: 'request',
    width: 350,
    render: (value) => value?.paymentCompany?.companyName || '_',
  },
  {
    title: messages['hmrcUpdates.hmrcId'],
    dataIndex: 'data',
    width: 250,
    render: (data) => data?.WorksNumber || '_',
  },
  {
    title: messages['hmrcUpdates.nxsysId'],
    width: 250,
    render: (record) =>
      record?.payId ||
      record?.previousPayrollNo ||
      (record.subcontractorRef ? record?.data?.WorksNumber : '_'),
  },
  {
    title: messages['table.employeeName'],
    width: 250,
    render: (record) => {
      const { data, subcontractorRef } = record;
      return (
        subcontractorRef?.fullName ||
        `${subcontractorRef?.forename || data?.Name?.Forename || ''} ${subcontractorRef?.surname || data?.Name?.Surname || ''}`.trim() ||
        '_'
      );
    },
  },
  {
    title: messages['table.niNumber'],
    width: 250,
    dataIndex: 'NINumber',
    render: (value) => value || '_',
  },
  {
    title: messages['hmrcUpdates.p6p9.dateIssued'],
    width: 250,
    dataIndex: 'issuedDate',
    render: (value) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  },
  {
    title: messages['common.type'],
    width: 150,
    dataIndex: 'type',
    render: (value: string) => (value ? <Tag>{value}</Tag> : '_'),
  },
  {
    title: messages['common.startDate'],
    width: 250,
    dataIndex: 'startDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },
  {
    title: messages['common.endDate'],
    width: 250,
    dataIndex: 'endDate',
    render: (value: string) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '_',
  },
  {
    title: messages['hmrcUpdates.studentLoan.studentLoanPlanType'],
    width: 250,
    dataIndex: 'planType',
    render: (value) => (value ? <Tag>{value}</Tag> : '_'),
  },
  {
    title: messages['hmrcUpdates.p6p9.appliedCancelled'],
    width: 200,
    dataIndex: 'status',
    render: (value) => (value ? value[0] : '_'),
  },
  {
    title: messages['hmrcUpdates.p6p9.dateAppliedCancelled'],
    width: 250,
    dataIndex: 'dateAppliedOrCancelled',
    render: (value) =>
      value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
  },
];
